// Product Control Plane — overview.
//
// The landing screen answers four questions and nothing else: what is live,
// what is draft, what changed, what needs attention. Everything below is in
// service of those four; anything that answered a fifth was cut.

import { useCallback, useEffect, useState } from "react";
import { listProducts, type RegistryResponse } from "../controlPlane/api";
import { CpHeader, Empty, ErrorNote, LaneChip } from "../controlPlane/ui";
import { laneHost, useControlPlaneTheme } from "../controlPlane/theme";
import {
  Banner,
  Button,
  CardGrid,
  Disclosure,
  EmptyState,
  SectionLabel,
  StatCard,
  Stack,
} from "../ui/primitives";
import { ProductCard } from "../ui/ProductCard";
import { TileGrid } from "../ui/ActionTile";
import { IconProducts } from "../ui/icons";

function describeError(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function ControlPlaneOverview() {
  const [data, setData] = useState<RegistryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { themeProps, toggle, theme } = useControlPlaneTheme();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await listProducts());
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const products = data?.products ?? [];
  const live = products.filter((p) => p.currentRevisionId !== null).length;
  const drafts = products.filter((p) => p.hasUnpublishedDraft).length;
  const unpublished = products.filter((p) => p.currentRevisionId === null).length;

  return (
    <div {...themeProps} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <CpHeader
        title="Product Control Plane"
        subtitle="What Gao applications render. Never where they connect."
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Always rendered, even before the registry loads. The other
                Control Plane screens already show UNKNOWN when the lane is
                unresolved; omitting the chip here made the LANDING screen the
                one place that said nothing at all. Silence is not a lie, but
                a stated UNKNOWN is the thing an operator can act on. */}
            <LaneChip lane={data?.lane ?? null} host={laneHost(data?.lane ?? null)} />
            <Button size="sm" onClick={toggle}>
              {theme === "light" ? "Dark" : "Light"}
            </Button>
            <Button size="sm" onClick={() => void load()} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </div>
        }
      />

      {error !== null && <ErrorNote>{error}</ErrorNote>}

      {data && !data.laneRecognized && (
        // A misconfigured lane means the console is serving PRODUCT content
        // from a Worker that could not name its own issuer. That is the one
        // condition on this screen that must never be collapsed, so it stays
        // a full-width banner above the numbers rather than a chip beside them.
        <Banner tone="danger" title="Lane not recognised — serving PRODUCT content">
          This Worker&rsquo;s <code>AUTH_V2_ISS</code> is{" "}
          <code>{data.issuer || "(empty)"}</code>, which is not one of the three known
          issuers. The Control Plane has failed closed to the <b>PRODUCT</b> lane — the
          strictest reading — so only <code>production</code> content is being served.
          This is a configuration fault, not a content one.
        </Banner>
      )}

      <CardGrid min={186} gap={12}>
        <StatCard label="Products" value={products.length} />
        <StatCard label="Live revisions" value={live} />
        <StatCard
          label="Unpublished drafts"
          value={drafts}
          tone={drafts > 0 ? "warn" : undefined}
          hint={drafts > 0 ? "Edited since the last publish." : undefined}
        />
        <StatCard
          label="Never published"
          value={unpublished}
          tone={unpublished > 0 ? "warn" : undefined}
          hint={unpublished > 0 ? "No live revision in this lane yet." : undefined}
        />
      </CardGrid>

      <Stack gap={12}>
        <SectionLabel>Products registered in this lane</SectionLabel>
        {loading && data === null ? (
          <Empty>Loading…</Empty>
        ) : data === null ? (
          // A failed load must never render as an empty registry: "no products
          // are registered" and "the request did not arrive" are opposite
          // facts, and showing the first when the second is true tells an
          // operator the Control Plane is empty when it is merely unreachable.
          <Empty>Could not load the registry — see the error above.</Empty>
        ) : products.length === 0 ? (
          <EmptyState
            title="No products registered in this lane"
            icon={<IconProducts size={22} />}
          >
            A product appears here once its first revision has been bootstrapped.
          </EmptyState>
        ) : (
          <TileGrid>
            {products.map((p) => (
              <ProductCard key={p.productId} product={p} lane={data.lane ?? undefined} />
            ))}
          </TileGrid>
        )}
      </Stack>

      {/* Three paragraphs of scope doctrine. It is genuinely load-bearing —
          the third one is why an operator cannot repoint an app's backend from
          here — but it is reference material an operator reads once, and as a
          permanent card it was the largest block on the landing screen. */}
      <Disclosure summary="What this console can and cannot change">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            color: "var(--fg-dim)",
            paddingTop: 10,
          }}
        >
          <div>
            <b style={{ color: "var(--accent)" }}>Control Plane — editable.</b>{" "}
            The catalog rows a product renders: title, url, icon, category,
            ordering, environment visibility, minimum app version.
          </div>
          <div>
            <b style={{ color: "var(--fg)" }}>Source-managed — read-only here.</b>{" "}
            Marketing, legal, privacy and support pages ship from their own
            repositories and change by deploy, not by publish.
          </div>
          <div>
            <b style={{ color: "var(--fg)" }}>Never.</b> An application&rsquo;s
            backend host or lane. An app compiles its lane and resolves a host
            from it before it can call the Control Plane at all, so the routing
            decision is already behind us by the time any document is read. The
            document carries no host field and the validator refuses one by name.
          </div>
        </div>
      </Disclosure>
    </div>
  );
}
